import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  DropDown,
  MLPVersion,
  Modal,
  TableWithSearchTemplate,
  Theme,
} from '../../../ui/components';
import { LoggerService, dateTimeToCustomFormat } from '../../../ui/utils';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { DynamicObjectInterface } from '../../../sdk/type';
import { useAPICall } from '../../../hooks';
import CoachService from '../../../sdk/services/coachService';
import CampaignService from '../../../sdk/services/campaignService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import getCoachListConfig, { dataSearchOption } from './coachListConfig';
import { CoachData } from './edit/[recId]';
import styles from './index.module.scss';

export const AdminCoachList = () => {
  interface CampaignOption {
    campaignId: string;
    campaignName: string;
    campaignStart: string;
    campaignEnd: string;
    status: string;
    title: string;
    value: string;
  }

  interface MasterData {
    campaign: DynamicObjectInterface[];
  }

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRowId, setSelectedRowId] = useState('');
  const [selectedCoach, setSelectedCoach] = useState<CoachData | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [data, setData] = useState([]);
  const [masterData, setMasterData] = useState<MasterData | null>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption>();
  const [displayEmpty, setDisplayEmpty] = useState(false);

  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (masterData) {
      setCampaignList(
        masterData.campaign
          ?.map(
            c =>
              ({
                ...c,
                title: `${c.campaignId} - ${c.campaignName}`,
                value: c.campaignId ?? '',
              }) as CampaignOption,
          )
          .sort((a, b) => {
            if (a.campaignStart > b.campaignStart) return -1;
            if (a.campaignStart < b.campaignStart) return 1;
            return 0;
          }),
      );
    }
  }, [masterData]);

  useEffect(() => {
    if (campaignList?.length) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';
      if (campaignId) {
        setSelectedCampaign(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchCoachList();
    }
  }, [selectedCampaign]);

  const fetchMasterData = async () => {
    let list = {
      campaign: {
        endpoint: CampaignService.getCampaigns,
      },
    };
    try {
      const results = await Promise.all(Object.values(list)?.map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data?.filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'CHALLENGE'
          );
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve campaign list', error);
    }
  };

  const fetchCoachList = async () => {
    try {
      const response = await apiCall(CoachService.getCoachList);
      const allData = response?.data?.data ?? [];
      // Filter by selected campaign
      const filteredData = allData.filter(
        (item: DynamicObjectInterface) => item.campaignId === selectedCampaign?.campaignId,
      );
      setData(filteredData);
      setDisplayEmpty(filteredData.length === 0);
    } catch (error) {
      LoggerService.log('Failed to retrieve coach list', error);
      setDisplayEmpty(true);
    }
  };

  const onEdit = (id: string) => {
    navigate(`/admin/coach-setup/edit?=${id}`);
  };

  const onClick = async (id: string) => {
    try {
      const response = await apiCall(CoachService.getCoachDetails, `${id}`);
      const result = response?.data;

      if (result.data) {
        setSelectedRowId(id);
        setSelectedCoach(result.data as CoachData);
        setShowDelete(true);
      } else {
        setSelectedCoach(null);
        LoggerService.log('Failed to retrieve coach details');
      }
    } catch (error) {
      LoggerService.log('Error fetching coach details', error);
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await apiCall(CoachService.getCoachExportCSV);

      if (response.status === 200) {
        const data = response.data;
        const blob = new Blob([data], { type: 'text/csv' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', 'coachesList.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release the object URL resources
        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      LoggerService.log('Error exporting CSV', error);
    }
  };

  const handleInputChange = (value: DynamicObjectInterface) => {
    navigate(`?campaignId=${value.campaignId}`);
  };

  const exportButtonConfig = [
    {
      title: t('admin.coachMaster.exportCSV'),
      buttonStyle: ButtonStyles.OUTLINE,
      onClick: exportToCSV,
    },
  ];

  const buttonConfig = [
    {
      title: t('admin.coachMaster.addCoach'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/coach-setup/edit?=new`);
      },
    },
  ];

  const onDeleteAction = async () => {
    try {
      const response = await apiCall(CoachService.deleteCoach, `${selectedRowId}`);
      if (response.status === 200) {
        // Assuming the server responds with success status
        LoggerService.log('Coach deleted successfully');
        adminToast({
          message: 'Coach deleted successfully',
          theme: Theme.SUCCESS,
        });
        fetchCoachList();
      } else {
        LoggerService.log('Failed to delete coach');
      }
    } catch (error) {
      LoggerService.log('Error deleting coach', error);
    } finally {
      setShowDelete(false);
    }
  };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.coachMaster.deleteCoach')} ${selectedCoach?.name}?`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.coachMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.coachMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.coachMaster.title')} button={[...buttonConfig, ...exportButtonConfig]} />
      <div className={styles['coach-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaign?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedOption: DropDownData) => {
              handleInputChange(selectedOption);
            },
          }}
          readOnly={false}
        />
        <div className={styles['coach-list-top-layout']}>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.period')}:</div>
            <p>
              {selectedCampaign?.campaignStart && selectedCampaign.campaignEnd
                ? `${dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY')} - ${dateTimeToCustomFormat(
                    selectedCampaign.campaignEnd,
                    'DD MMM YYYY',
                  )}`
                : '-'}
            </p>
          </div>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.status')}:</div>
            <p>{selectedCampaign?.status ?? '-'}</p>
          </div>
        </div>
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={data}
          onReset={() => void 0}
          searchByStrip
          getColumns={getCoachListConfig}
          data={dataSearchOption}
          rowKey="recId"
          selected={selectedRowId}
          cb={{ onEdit, onClick }}
          onRowClick={row => {
            setSelectedRowId(row.recId);
          }}
        />
      )}
      {deletePrompt}
    </div>
  );
};
